import * as React from "react";
import { observer } from "mobx-react";
import { Form } from "antd";
import TaskTypesVisualStore from "../stores/TaskTypesVisualStore";
import { useForm } from "antd/lib/form/Form";
import TaskTypeDialogForm from "./TaskTypeDialogForm";
import AppModal from "../../common/components/AppModal";

type Props = {
    store: TaskTypesVisualStore;
};

const TaskTypeEditDialog: React.FC<Props> = ({ store }) => {
    const [form] = useForm();
    React.useEffect(() => {
        form.resetFields();
    }, [store.typeEditDialogVisible]);
    if (!store.selectedTaskType) {
        return null;
    }

    const handleSubmit = async () => {
        form.validateFields()
            .then((values) => {
                const dueDate = !values.count
                    ? null
                    : { count: values.count, period: values.period };
                const reminderDays =
                    dueDate && values.reminderDays ? values.reminderDays : null;
                store.editTaskType(
                    values.name,
                    values.assignee,
                    values.code,
                    values.canChangeStatusManually,
                    store.selectedTaskType?.distinctSubtaskStatuses ?? false,
                    dueDate,
                    reminderDays,
                );
            })
            .catch((err) => {
                console.log(err);
            });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const taskTypeNamesValidator = (rule: any, value: any, callback: any) => {
        const taskTypesToValidate = store!.taskTypes.map((t) =>
            t.name.toLowerCase(),
        );
        if (
            value &&
            taskTypesToValidate.includes(value.toLowerCase()) &&
            store.selectedTaskType?.name.toLowerCase() !== value.toLowerCase()
        ) {
            callback("Task type with the same name already exists");
        } else {
            callback();
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const taskTypesCodesValidator = (rule: any, value: any, callback: any) => {
        const taskTypesToValidate = store!.taskTypes.map(
            (t) => t.code?.toLowerCase() ?? "",
        );
        if (
            value &&
            taskTypesToValidate.includes(value.toLowerCase()) &&
            store.selectedTaskType?.code?.toLowerCase() !== value.toLowerCase()
        ) {
            callback("Task type with the same code already exists");
        } else {
            callback();
        }
    };

    return (
        <AppModal
            data-id-type="modal-window"
            data-id-name="update type"
            className="alpha-modal task-type-create-modal"
            centered
            title="Edit task type"
            open={store.typeEditDialogVisible}
            onCancel={() => {
                store.selectType(undefined);
            }}
            closable={false}
            width={520}
            cancelButtonTitle="Cancel"
            onCancelClick={() => {
                store.selectType(undefined);
            }}
            cancelButtonProps={{
                "data-id": "button-edit-task-type-cancel",
            }}
            submitButtonTitle="Save"
            onSubmitClick={handleSubmit}
            submitButtonProps={{
                "data-id": "button-edit-task-type-Save",
            }}
        >
            <Form
                layout="vertical"
                data-id="create-task-type-dialog-form"
                form={form}
                className="alpha-form"
                initialValues={{
                    ...store.selectedTaskType,
                    ...{
                        count: store.selectedTaskType.dueDate?.count,
                        period: store.selectedTaskType.dueDate?.period ?? "D",
                        reminderDays: store.selectedTaskType.reminderDays,
                    },
                }}
            >
                <TaskTypeDialogForm
                    form={form}
                    assigneeFieldName="assignee"
                    assigneeInitialValue={store.selectedTaskType.assignee}
                    assigneeUsers={store.filteredUsersForProject}
                    showInactiveAssigneeStatus
                    nameRules={[
                        {
                            required: true,
                            message: "Name is required",
                            whitespace: true,
                        },
                        { validator: taskTypeNamesValidator },
                    ]}
                    codeRules={[{ validator: taskTypesCodesValidator }]}
                />
            </Form>
        </AppModal>
    );
};

export default observer(TaskTypeEditDialog);
